import { getYear, subYears } from 'date-fns';
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { TAX_REPORT_STATUS, TAX_REPORT_STATUS_TITLES } from '_constants/taxFilingInfo';
import { theme } from '_constants/theme';
import { selectTaxFilingStatuses } from '_store/order/selector';

import Accordion from 'components/atomic/Accordion';
import CheckList from 'components/atomic/CheckList';
import Icon from 'components/atomic/Icon';
import Card from 'components/atomic/layouts/Card';
import Text from 'components/atomic/layouts/Text';

const initialBusinessItems = [
  {
    id: TAX_REPORT_STATUS.previousYearReturnUploaded,
    title: TAX_REPORT_STATUS_TITLES[TAX_REPORT_STATUS.previousYearReturnUploaded],
    hint: null,
    completed: false,
  },
  {
    id: TAX_REPORT_STATUS.verifyAllTransactionsImported,
    title: TAX_REPORT_STATUS_TITLES[TAX_REPORT_STATUS.verifyAllTransactionsImported],
    hint: null,
    completed: false,
  },
  {
    id: TAX_REPORT_STATUS.bookkeeperReview,
    title: TAX_REPORT_STATUS_TITLES[TAX_REPORT_STATUS.bookkeeperReview],
    hint: null,
    completed: false,
  },
  {
    id: TAX_REPORT_STATUS.taxPreparerReview,
    title: TAX_REPORT_STATUS_TITLES[TAX_REPORT_STATUS.taxPreparerReview],
    hint: null,
    completed: false,
  },
  {
    id: TAX_REPORT_STATUS.sentForSignature,
    title: TAX_REPORT_STATUS_TITLES[TAX_REPORT_STATUS.sentForSignature],
    hint: null,
    completed: false,
  },
  {
    id: TAX_REPORT_STATUS.filed,
    title: TAX_REPORT_STATUS_TITLES[TAX_REPORT_STATUS.filed],
    hint: null,
    completed: false,
  },
];

const initialPersonalItems = [
  {
    id: TAX_REPORT_STATUS.previousYearReturnUploaded,
    title: TAX_REPORT_STATUS_TITLES[TAX_REPORT_STATUS.previousYearReturnUploaded],
    hint: null,
    completed: false,
  },
  {
    id: TAX_REPORT_STATUS.personalInformationConfirmed,
    title: TAX_REPORT_STATUS_TITLES[TAX_REPORT_STATUS.personalInformationConfirmed],
    hint: null,
    completed: false,
  },
  {
    id: TAX_REPORT_STATUS.bookkeeperReview,
    title: TAX_REPORT_STATUS_TITLES[TAX_REPORT_STATUS.bookkeeperReview],
    hint: null,
    completed: false,
  },
  {
    id: TAX_REPORT_STATUS.taxPreparerReview,
    title: TAX_REPORT_STATUS_TITLES[TAX_REPORT_STATUS.taxPreparerReview],
    hint: null,
    completed: false,
  },
  {
    id: TAX_REPORT_STATUS.sentForSignature,
    title: TAX_REPORT_STATUS_TITLES[TAX_REPORT_STATUS.sentForSignature],
    hint: null,
    completed: false,
  },
  {
    id: TAX_REPORT_STATUS.filed,
    title: TAX_REPORT_STATUS_TITLES[TAX_REPORT_STATUS.filed],
    hint: null,
    completed: false,
  },
];

const createCheckListItems = ({ status, items }) => {
  const activeItemIndex = items.findIndex(({ id }) => id === status);

  return items.map((item, index) => {
    if (index <= activeItemIndex) {
      return { ...item, completed: true };
    }

    return item;
  });
};

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledBodyContainer = styled.div``;

const TaxMonitoringView = () => {
  const { business: businessStatus, personal: personalStatus } = useSelector(selectTaxFilingStatuses);

  return (
    <Card>
      <StyledHeader>
        <Text size={theme.fontSizes.medium} weight={600} bottomSpace={theme.spaces.large}>
          {getYear(subYears(new Date(), 1))} Tax Return Filing Monitoring
        </Text>
      </StyledHeader>
      <Accordion
        openIcon={<Icon alias={ICON_ALIASES.chevronUp} color={theme.colors.gray.secondary} />}
        closeIcon={<Icon alias={ICON_ALIASES.chevronDown} color={theme.colors.gray.secondary} />}
        items={[
          {
            openByDefault: true,
            title: (
              <Text size={theme.fontSizes.small} weight={600} color={theme.colors.gray.primary}>
                Part 1 - Business Tax Return
              </Text>
            ),
            body: (
              <StyledBodyContainer>
                <Text
                  size={theme.fontSizes.small}
                  weight={400}
                  color={theme.colors.gray.secondary}
                  bottomSpace={theme.spaces.regular}
                >
                  The list of statuses below provides context of what stage your tax return is in, and whether you need
                  to take any action to help things along.
                </Text>
                <CheckList items={createCheckListItems({ items: initialBusinessItems, status: businessStatus })} />
              </StyledBodyContainer>
            ),
          },
          {
            openByDefault: true,
            title: (
              <Text size={theme.fontSizes.small} weight={600} color={theme.colors.gray.primary}>
                Part 2 - Personal Tax Return
              </Text>
            ),
            body: (
              <StyledBodyContainer>
                <Text
                  size={theme.fontSizes.small}
                  weight={400}
                  color={theme.colors.gray.secondary}
                  bottomSpace={theme.spaces.regular}
                >
                  The list of statuses below provides context of what stage your tax return is in, and whether you need
                  to take any action to help things along.
                </Text>
                <CheckList items={createCheckListItems({ items: initialPersonalItems, status: personalStatus })} />
              </StyledBodyContainer>
            ),
          },
        ]}
      />
    </Card>
  );
};

export default TaxMonitoringView;
